import { Box, Card } from '@mui/material';
import SearchBar from './form/SearchBar';
import { useCallback, useEffect, useMemo, useRef, useState, ForwardedRef, forwardRef, useImperativeHandle } from 'react';
import { AgGridReact, AgGridReactProps } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css"; // Core grid CSS, always needed
import "@ag-grid-community/styles/ag-theme-material.css"; // Optional theme CSS
import '../styles/ag-theme-acmecorp.css';
import RowActionsGrid from './table/RowActionsGrid';
import { ColDef, RowDragEndEvent, IRowNode, CellKeyDownEvent } from "@ag-grid-community/core";
import DownloadButton from './form/DownloadButton';

interface Props extends AgGridReactProps {
  columns: any[],
  data: any[],
  idKey: string,
  searchable: boolean,
  moreInfo?: any,
  actions?: any,
  page?: string,
  tab: string,
  showHide: any[],
  editSuppressCell?: boolean,
  focusSuppressCell?: boolean,
  downloadable?: boolean,
  /** Define if the table rows are draggable. */
  isDraggable?: boolean,
  /** Reference to the HTML Element where to drop selected rows. */
  dragZone?: HTMLElement | null,
  /** Callback function when dragging has stopped */
  onDragStop?: any,
  rowClassRules?: any,
  defaultColDef?: ColDef,
  onRowDoubleClicked?: any,
  cursorHoverOption?: string,
  onCellKeyDown?: (e: CellKeyDownEvent) => void
}

const defaultProps: Partial<Props> = {
    isDraggable: false,
};

export interface RowActionProps {
  rowKey: string;
  viewSelect: (rowKey: string, row: any, editMode?: boolean) => void;
  rowData: any;
  close: any;
}

const LassoGrid = forwardRef(function LassoGrid(props: Props, outerGridRef: ForwardedRef<AgGridReact>) {
    const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
    const [searchValue, setSearchValue] = useState<string>('');
    const [selectedRow, setSelectedRow] = useState<any>({});
    const [selectedKey, setSelectedKey] = useState<string>('');
    const [selectedEdit, setSelectedEdit] = useState<boolean>(false);
    const gridRef = useRef<AgGridReact>(null);
    useImperativeHandle(outerGridRef, () => gridRef.current!, []);

    props = { ...defaultProps, ...props };

    const rowClicked = (
        rowKey: string,
        row: any,
        e: any,
        editMode?: boolean,
    ) => {
        const field = e.colDef.field;
        if (field !== 'actions') {
            if (props.moreInfo) {
                setSelectedRow(row);
                setSelectedKey(rowKey);
                setSelectedEdit(editMode ?? false);
                gridRef.current?.api.setColumnsVisible(
                    props.showHide,
                    false,
                );
            }
        }
    };

    const actionClicked = (
        rowKey: string,
        row: any,
        e: any,
        editMode?: boolean,
    ) => {
        if (props.moreInfo) {
            setSelectedRow(row);
            setSelectedKey(rowKey);
            setSelectedEdit(editMode ?? false);
        }
    };

    const closeDetails = () => {
        setSelectedRow({});
        setSelectedKey('');
        setSelectedEdit(false);
        gridRef.current?.api.setColumnsVisible(props.showHide, true);
        gridRef.current?.api.setColumnsVisible(props.showHide, true);
        gridRef.current?.api.deselectAll();
    };

    useEffect(() => {
        gridRef.current?.api?.setGridOption("quickFilterText", searchValue);
    }, [searchValue]);

    useEffect(() => {
        setSelectedRow({});
        setSelectedKey('');
        if (props.page === 'users') {
            const columnDefs = gridRef.current?.api?.getColumnDefs();
            columnDefs?.forEach(function(colDef: any) {
                if (colDef.field === 'actions') {
                    colDef.cellRenderer = RowActionsGrid;
                    colDef.cellRendererParams = {
                        actions: props.actions,
                        viewSelect: actionClicked,
                    };
                }
            });
            columnDefs && gridRef.current!.api.setGridOption('columnDefs', columnDefs);
        }
    }, [props.data]);

    const onGridReady = useCallback((params: any) => {
        if (props.isDraggable) {
            const dropZone = {
                getContainer: () => props.dragZone,
                onDragStop: (param: RowDragEndEvent) => {
                    const dropped = param.nodes.map((node: IRowNode) => node.data);
                    props.onDragStop(dropped);
                    params.api.deselectAll();
                }
            };
            params.api.addRowDropZone(dropZone);
        }
    }, [props.dragZone]);

    if (props.enableAdvancedFilter) {
        gridRef.current?.api?.setGridOption("advancedFilterParent", document.getElementById("gridAdvancedSearch"));
    }

    const tabularParams = {
        columnKeys: gridRef?.current?.api?.getAllDisplayedColumns()
            .filter((column) => column.getColId() !== "ag-Grid-ControlsColumn"),
        processCellCallback: function(cell: any) {
            if (typeof cell.value === 'object' && cell.value !== null) {
                return JSON.stringify(cell.value, null, 2);
            }
            return cell.value;
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                height: '100%',
                pb: 2,
            }}
        >
            <Box
                sx={{
                    flexGrow: 2,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                        pb: 2,
                    }}
                >
                    {props.downloadable && <DownloadButton gridRef={gridRef} />}
                    {props.searchable &&
            <SearchBar
                showBuilder={props.enableAdvancedFilter ? (
                    () => gridRef.current?.api?.showAdvancedFilterBuilder()
                ) : undefined}
                onChange={(value: string) => setSearchValue(value)}
            />
                    }
                </Box>
                <div
                    className="ag-theme-material ag-theme-common ag-theme-acmecorp"
                    style={gridStyle}
                >
                    <AgGridReact
                        {...props}
                        ref={gridRef}
                        rowData={props.data}
                        columnDefs={props.columns}
                        suppressClickEdit={
                            props.editSuppressCell
                                ? props.editSuppressCell
                                : true
                        }
                        suppressCellFocus={'focusSuppressCell' in props ? props.focusSuppressCell : true}
                        onCellClicked={props.onCellClicked ? props.onCellClicked : (e) => rowClicked(e.data.id, e.data, e)}
                        onCellKeyDown={props?.onCellKeyDown ? props.onCellKeyDown : () => {}}
                        onGridReady={onGridReady}
                        rowDragMultiRow={props.isDraggable}
                        rowDragEntireRow={props.isDraggable}
                        rowClassRules={props.rowClassRules}
                        onRowDoubleClicked={props.onRowDoubleClicked ? props.onRowDoubleClicked : () => {}}
                        defaultColDef={props.defaultColDef}
                        defaultCsvExportParams={tabularParams}
                        defaultExcelExportParams={tabularParams}
                        getRowStyle={() => ({
                            cursor: props.cursorHoverOption ?? 'auto'
                        })}
                    />
                </div>
            </Box>
            {selectedKey !== '' && props.moreInfo && (
                <Card
                    sx={{
                        padding: 2,
                        maxHeight: '100%',
                        overflow: 'auto',
                        width: '400px',
                    }}
                >
                    <props.moreInfo
                        data={selectedRow}
                        edit={selectedEdit}
                        close={closeDetails}
                    />
                </Card>
            )}
        </Box>
    );
});

export default LassoGrid;
